import React, { useState, useEffect } from 'react';
import PageLayout from '../../components/PageLayout';
import Toggle from 'react-toggle';
import FormInput from '../../components/FormInput';
import Toast from '../../components/Toast';
import CaretIcon from '../../icons/rev_carat.png';
import HeaderIconSvg from '../../icons/newicons/arrow-down-up.svg';

import {
  GET_USER_CREDITS_URL,
  UPDATE_USER_CREDITS_URL,
  getData,
  putData,
} from '../../utils/api';

import { getLoginDetails } from '../../utils/storage';

const UserCredits = (props) => {
  const { companyId } = props.match.params;
  const companyName = props.location?.state?.companyName || '';
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toastParams, setToastParams] = useState({
    message: '',
    type: '',
    id: '',
  });

  useEffect(() => {
    if (companyId) {
      fetchUsers();
    }
  }, [companyId]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getData(GET_USER_CREDITS_URL, {
        companyId: companyId,
      });
      
      // Transform API response to match component structure
      const usersList = (data.list || data || []).map((user) => ({
        userId: user.userId || user.id,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        role: user.role || user.roleName || 'User',
        lookupCount: user.lookupCount || user.lookupCredits || 0,
        balance: user.balance || 0,
        isUnlimited: user.isUnlimited || false,
      }));
      
      setUsers(usersList);
      setIsLoading(false);
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to fetch users',
        id: new Date().getTime(),
      });
      setIsLoading(false);
    }
  };

  const handleLookupCountChange = (userId, newValue) => {
    // Update local state immediately for better UX
    // Allow empty string so user can clear the field
    setUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.userId === userId
          ? { ...user, lookupCount: newValue === '' ? '' : (parseInt(newValue) || 0) }
          : user
      )
    );
  };

  const handleSaveCount = async (userId, count) => {
    try {
      const countValue = count === '' ? 0 : (parseInt(count) || 0);
      await putData(UPDATE_USER_CREDITS_URL, {
        companyId: companyId,
        userId: userId,
        count: countValue,
      });

      // Refresh data from server after successful update
      await fetchUsers();

      setToastParams({
        type: 'SUCCESS',
        message: 'Count updated successfully',
        id: new Date().getTime(),
      });
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to update count',
        id: new Date().getTime(),
      });
      // Refresh data on error to ensure UI is in sync
      fetchUsers();
    }
  };

  const handleUnlimitedToggle = async (userId, isUnlimited) => {
    try {
      await putData(UPDATE_USER_CREDITS_URL, {
        companyId: companyId,
        userId: userId,
        isUnlimited: isUnlimited,
      });

      // Refresh data from server after successful update
      await fetchUsers();

      setToastParams({
        type: 'SUCCESS',
        message: 'Unlimited status updated successfully',
        id: new Date().getTime(),
      });
    } catch (e) {
      console.log('ERROR', e);
      setToastParams({
        type: 'ERROR',
        message: e && e.error ? e.error : 'Failed to update unlimited status',
        id: new Date().getTime(),
      });
      // Refresh data on error to ensure UI is in sync
      fetchUsers();
    }
  };

  return (
    <PageLayout
      icon={HeaderIconSvg}
      heading="User Credits Management"
      searchPlaceholder="Search Users"
      hideSearchBar={true}
    >
      <div
        className="flex flex-middle c-pointer"
        onClick={() => {
          props.history.goBack();
        }}
        style={{ marginBottom: '20px' }}
      >
        <img
          src={CaretIcon}
          className="wt-20 rotate-90 op-5 pr-8"
          alt="Back"
        />
        <span className="fs-12 c-table-header fw-bolder">BACK TO CUSTOMERS</span>
      </div>
      {companyName && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: '500', color: '#6c757d', marginRight: '8px' }}>
            Customer:
          </span>
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#212529' }}>
            {companyName}
          </span>
        </div>
      )}
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          marginTop: '20px',
        }}
      >
        {isLoading ? (
          <div className="flex flex-middle flex-center" style={{ padding: '80px' }}>
            <span className="c-gray fs-14">Loading...</span>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr 1fr',
                gap: '20px',
                padding: '20px 24px',
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #e9ecef',
                alignItems: 'center',
              }}
            >
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                User Name
              </div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Role
              </div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Balance
              </div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Count
              </div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>
                Is Unlimited?
              </div>
            </div>

            {/* Table Body */}
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {users.length === 0 ? (
                <div className="flex flex-middle flex-center" style={{ padding: '60px' }}>
                  <span className="c-gray fs-14">No users found</span>
                </div>
              ) : (
                users.map((user, index) => (
                  <div
                    key={user.userId || index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1.5fr 1.5fr 1.5fr 1fr',
                      gap: '20px',
                      padding: '20px 24px',
                      alignItems: 'center',
                      borderBottom: index < users.length - 1 ? '1px solid #e9ecef' : 'none',
                      backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fa',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#f8f9fa';
                    }}
                  >
                    {/* User Name */}
                    <div style={{ fontWeight: '500', fontSize: '15px', color: '#212529' }}>
                      {user.name}
                    </div>

                    {/* Role */}
                    <div>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          backgroundColor: user.role === 'Admin' ? '#e7f3ff' : '#f0f0f0',
                          color: user.role === 'Admin' ? '#0066cc' : '#495057',
                        }}
                      >
                        {user.role}
                      </span>
                    </div>

                    {/* Balance */}
                    <div style={{ fontWeight: '500', fontSize: '15px', color: '#212529' }}>
                      {user.balance || 0}
                    </div>

                    {/* Count */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <FormInput
                        inputClassName="bg-blue rounded-pill"
                        style={{
                          paddingLeft: '16px',
                          paddingRight: '16px',
                          width: '100%',
                          maxWidth: '140px',
                          height: '40px',
                          fontSize: '14px',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          backgroundColor: '#fff',
                          transition: 'all 0.2s ease',
                        }}
                        type="number"
                        min="0"
                        value={user.lookupCount === '' ? '' : user.lookupCount}
                        onChange={(e) => {
                          handleLookupCountChange(user.userId, e.target.value);
                        }}
                      />
                      <div
                        onClick={() => handleSaveCount(user.userId, user.lookupCount)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          cursor: 'pointer',
                          borderRadius: '6px',
                          backgroundColor: '#00965a',
                          transition: 'all 0.2s ease',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#007a47';
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '#00965a';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                        title="Save count"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          fill="white"
                          viewBox="0 0 16 16"
                          style={{ display: 'block' }}
                        >
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                      </div>
                    </div>

                    {/* Is Unlimited Toggle */}
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Toggle
                        className="bg-theme"
                        checked={user.isUnlimited}
                        onChange={(e) => {
                          handleUnlimitedToggle(user.userId, e.target.checked);
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      <Toast
        type={toastParams.type}
        message={toastParams.message}
        id={toastParams.id}
      />
    </PageLayout>
  );
};

export default UserCredits;
